import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Forbidden } from '@/modules/auth/Forbidden';
import { NotFound } from '@/shared/ui/NotFound';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useAuth } from '@/modules/auth/AuthContext';
import { useVenueContext } from '@/modules/venue/VenueContext';
import {
  sessionDurationOptions,
  venueSlotGranularityMinutes,
} from '@/modules/venue/slotGranularity';
import { useRooms } from '@/modules/venues/rooms';
import { useEditPass, usePass, type EditVariationInput } from './passes';
import { RoomCoverageField } from './RoomCoverageField';
import { depositDraftFrom, depositInput, emptyDeposit, type DepositDraft } from './DepositFields';
import {
  SalesAvailabilityFields,
  emptySalesAvailability,
  salesAvailabilityDraftFrom,
  salesAvailabilityInput,
  type SalesAvailabilityDraft,
} from './SalesAvailabilityFields';
import {
  AdditionalOptionsFields,
  additionalOptionsDraftFrom,
  additionalOptionsPayload,
  emptyAdditionalOptions,
  type AdditionalOptionsDraft,
} from './AdditionalOptionsFields';

interface VariationRow {
  id?: string;
  name: string;
  amount: string;
  requiresWaiver: boolean;
  /** Whole hours as typed; only a STANDARD pass sends it (ADR 0072 §2). */
  hours: string;
  active: boolean;
}

function editErrorMessage(t: ReturnType<typeof useTranslation>['t'], error: unknown): string {
  if (error instanceof ApiError && error.code) {
    const key = `products.errors.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return t('products.errors.generic');
}

export function EditPassPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuthorization();
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const { passId } = useParams();

  const scope = { kind: 'venue', venueId } as const;
  const canManage = auth.can('products.pass.manage', scope);

  const venue = venues.find((candidate) => candidate.id === venueId);
  const currency = venue?.currency ?? '';
  const slotGranularityMinutes = venueSlotGranularityMinutes(venue?.slotGranularityMinutes);
  const durationOptions = sessionDurationOptions(slotGranularityMinutes);

  const passQuery = usePass(venueId, passId ?? '', canManage);
  const pass = passQuery.data ?? null;
  const editPass = useEditPass(venueId, passId ?? '');
  const roomsQuery = useRooms(venueId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allRooms, setAllRooms] = useState(true);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const [variations, setVariations] = useState<VariationRow[]>([]);
  const [policy, setPolicy] = useState<DepositDraft>(emptyDeposit);
  const [sessionDurationMinutes, setSessionDurationMinutes] =
    useState<number>(slotGranularityMinutes);
  const [availability, setAvailability] = useState<SalesAvailabilityDraft>(emptySalesAvailability);
  const [options, setOptions] = useState<AdditionalOptionsDraft>(emptyAdditionalOptions);

  useEffect(() => {
    if (!pass) return;
    setName(pass.name);
    setDescription(pass.description ?? '');
    setAllRooms(pass.allRooms);
    setRoomIds(pass.roomIds);
    setPolicy(depositDraftFrom(pass.deposit));
    setSessionDurationMinutes(pass.sessionDurationMinutes ?? slotGranularityMinutes);
    setAvailability(salesAvailabilityDraftFrom(pass.salesAvailability));
    setOptions(additionalOptionsDraftFrom(pass));
    setVariations(
      pass.variations.map((variation) => ({
        id: variation.id,
        name: variation.name,
        amount: variation.amount,
        requiresWaiver: variation.requiresWaiver,
        hours: String(variation.hours),
        active: variation.active,
      })),
    );
  }, [pass, slotGranularityMinutes]);

  function updatePolicy(patch: Partial<DepositDraft>) {
    setPolicy((current) => ({ ...current, ...patch }));
  }

  function updateAvailability(patch: Partial<SalesAvailabilityDraft>) {
    setAvailability((current) => ({ ...current, ...patch }));
  }

  function updateOptions(patch: Partial<AdditionalOptionsDraft>) {
    setOptions((current) => ({ ...current, ...patch }));
  }

  if (!canManage) return <Forbidden />;
  if (passQuery.isLoading)
    return <p className="p-6 text-muted-foreground">{t('products.loading')}</p>;
  if (!pass) return <NotFound />;
  const standard = pass.type === 'STANDARD';

  function updateVariation(index: number, patch: Partial<VariationRow>) {
    setVariations((current) =>
      current.map((variation, i) => (i === index ? { ...variation, ...patch } : variation)),
    );
  }

  function addVariation() {
    setVariations((current) => [
      ...current,
      { name: '', amount: '', requiresWaiver: false, hours: '', active: true },
    ]);
  }

  function removeVariation(index: number) {
    setVariations((current) => current.filter((_, i) => i !== index));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const payload: EditVariationInput[] = variations.map((variation) => ({
      id: variation.id,
      name: variation.name,
      amount: variation.amount,
      requiresWaiver: variation.requiresWaiver,
      hours: standard ? Number(variation.hours) : undefined,
      active: variation.active,
    }));
    try {
      await editPass.mutateAsync({
        name,
        description: description.trim() ? description : undefined,
        allRooms,
        roomIds: allRooms ? [] : roomIds,
        sessionDurationMinutes: pass?.type === 'SESSION' ? sessionDurationMinutes : undefined,
        salesAvailability: salesAvailabilityInput(availability),
        ...(standard
          ? {}
          : { deposit: depositInput(policy), ...additionalOptionsPayload(options) }),
        variations: payload,
      });
      toast.success(t('products.toast.updated'));
      navigate(`/loc/${venueId}/products`);
    } catch (caught) {
      toast.error(editErrorMessage(t, caught));
    }
  }

  return (
    <section className="mx-auto grid max-w-2xl gap-6 p-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/loc/${venueId}/products`)}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('products.actions.back')}
        </button>
        <h1 className="text-2xl font-semibold">
          {t('products.form.editTitle', { type: t(`products.types.${pass.type}`) })}
        </h1>
      </div>

      <form onSubmit={submit} className="grid gap-5">
        <fieldset className="grid gap-3 rounded-md border p-4">
          <legend className="px-1 text-sm font-medium">{t('products.form.generalDetails')}</legend>

          <div className="grid gap-1">
            <Label htmlFor="pass-name">{t('products.form.name')}</Label>
            <Input
              id="pass-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('products.form.namePlaceholder')}
              required
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor="pass-description">{t('products.form.description')}</Label>
            <Input
              id="pass-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {pass.type === 'SESSION' && (
            <div className="grid gap-1">
              <Label htmlFor="pass-duration">{t('products.form.duration.label')}</Label>
              <select
                id="pass-duration"
                value={sessionDurationMinutes}
                onChange={(event) => setSessionDurationMinutes(Number(event.target.value))}
                className="h-9 w-48 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
              >
                {durationOptions.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {t(`products.form.duration.${minutes}`)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </fieldset>

        <RoomCoverageField
          allRooms={allRooms}
          roomIds={roomIds}
          rooms={roomsQuery.data?.items ?? []}
          onChange={(next) => {
            setAllRooms(next.allRooms);
            setRoomIds(next.roomIds);
          }}
        />

        <fieldset className="grid gap-3 rounded-md border p-4">
          <legend className="px-1 text-sm font-medium">{t('products.form.variations')}</legend>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="pb-1 font-medium">{t('products.form.variationName')}</th>
                <th className="pb-1 font-medium">
                  {t('products.form.price')} ({currency})
                </th>
                {standard && <th className="pb-1 font-medium">{t('products.form.hours')}</th>}
                <th className="pb-1 font-medium">{t('products.form.waiverRequired')}</th>
                <th className="pb-1 font-medium">{t('products.form.variationActive')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {variations.map((variation, index) => (
                <tr key={variation.id ?? `new-${index}`}>
                  <td className="py-1 pr-2">
                    <Input
                      aria-label={t('products.form.variationName')}
                      value={variation.name}
                      onChange={(event) => updateVariation(index, { name: event.target.value })}
                      placeholder={t('products.form.variationNamePlaceholder')}
                      required
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <Input
                      aria-label={t('products.form.price')}
                      inputMode="decimal"
                      value={variation.amount}
                      onChange={(event) => updateVariation(index, { amount: event.target.value })}
                      className="w-32"
                      required
                    />
                  </td>
                  {standard && (
                    <td className="py-1 pr-2">
                      <Input
                        aria-label={`${t('products.form.hours')} ${index + 1}`}
                        type="number"
                        min={1}
                        step={1}
                        value={variation.hours}
                        onChange={(event) => updateVariation(index, { hours: event.target.value })}
                        className="w-20"
                        required
                      />
                    </td>
                  )}
                  <td className="py-1 pr-2">
                    <input
                      type="checkbox"
                      aria-label={`${t('products.form.waiverRequired')} ${index + 1}`}
                      checked={variation.requiresWaiver}
                      onChange={(event) =>
                        updateVariation(index, { requiresWaiver: event.target.checked })
                      }
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="checkbox"
                      aria-label={t('products.form.variationActive')}
                      checked={variation.active}
                      onChange={(event) => updateVariation(index, { active: event.target.checked })}
                    />
                  </td>
                  <td className="py-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={t('products.actions.removeVariation')}
                      disabled={variations.length === 1}
                      onClick={() => removeVariation(index)}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <Button type="button" size="sm" variant="outline" onClick={addVariation}>
              <Plus className="size-4" aria-hidden="true" />
              {t('products.actions.addVariation')}
            </Button>
          </div>
        </fieldset>

        <SalesAvailabilityFields draft={availability} onChange={updateAvailability} />

        {!standard && (
          <AdditionalOptionsFields
            draft={options}
            onChange={updateOptions}
            depositDraft={policy}
            currency={currency}
            onDepositChange={updatePolicy}
          />
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={editPass.isPending}>
            {t('products.actions.save')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(`/loc/${venueId}/products`)}
          >
            {t('products.actions.cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
}
